import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes, EQuestionType } from '@hubertai/screening-question-types';
import { CREATE_MODES, ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';
import QuestionTypeDescription from '../QuestionTypeDescription';
import {
  applyMemberGroupChoice,
  canChangeOrganizationLevel,
  canSeeRecruiterScopePicker,
  chosenGroupsLackQuestionCreate,
  getOtherAccessGroupIds,
  getRecruiterScopeCorrections,
  getRecruiterScopeOptions,
  losesQuestionUpdate,
  picksAnyClientGroup,
} from './recruiterScope';
import type { TRecruiterScopeContext } from './recruiterScope';
import { getAdminScopeCorrections } from './adminScope';
import AccessGroupCombobox from 'components/comboboxes/AccessGroupCombobox';
import ClientCombobox from 'components/comboboxes/ClientCombobox';
import LanguageCombobox from 'components/comboboxes/LanguageCombobox';
import QuestionTypeCombobox from 'components/comboboxes/QuestionTypeCombobox';
import VisibilityCombobox from 'components/comboboxes/VisibilityCombobox';
import { useVisibilityLabels } from 'utils/visibilityLabels';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { useTemplateClientId, useTemplateReferenceId, useTemplateVisibility } from 'hooks/useTemplate';
import { useUser } from 'hooks/useUser';
import { useGetClientAccessGroupsQuery } from 'rtk/query/apis/clients';
import type { TVisibility } from 'types/template/template.types';
import type { AccessGroup } from 'types/client.types';
import type { TRecruiterAppUser } from 'types/user.types';
import { isHubertAdminUser, isRecruiterUser } from 'types/user.types';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  updateType,
  updateLanguage,
  updateVisibility,
  updateAccessGroupIds,
  updateClientId,
  setText,
} from 'rtk/Question.Editor.Slice';
import { selectQuestionMode, selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';

export const TypeSelector = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();

  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const user = useUser();
  const isRecruiter = isRecruiterUser(user);

  return (
    <div className={clsx(inTemplateEditAndUneditable && 'hidden', 'flex flex-col justify-start gap-4 p-1')}>
      <div
        className={clsx(
          inTemplateEditAndUneditable && 'hidden',
          'hubert-vertical-center flex flex-row justify-start gap-4',
        )}
      >
        <HubertLabel className="text-sm font-medium" htmlFor="create-question-type-select">
          {t('selectors.type.label')}
        </HubertLabel>
        <QuestionTypeCombobox
          id="create-question-type-select"
          isMulti={false}
          onValueChange={(value) => {
            dispatch(updateType({ questionId, type: value as EQuestionType }));
          }}
          disabled={mode && [ECreateQuestionMode.Edit].includes(mode)}
          value={type}
          showDescription={false}
        />
        <QuestionTypeDescription type={type} />
      </div>
      <div>
        <MutedText text={t('selectors.type.hint')} />
        {type === 'open' && isRecruiter && (
          <div className="mt-3 max-w-4xl space-y-2">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
              <p className="text-xs font-semibold text-amber-900">{t('selectors.type.important')}</p>
              <p className="mt-1 text-xs leading-5 text-amber-800">{t('selectors.type.importantText')}</p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5">
              <p className="text-xs font-semibold text-blue-900">{t('selectors.type.tip')}</p>
              <p className="mt-1 text-xs leading-5 text-blue-800">{t('selectors.type.tipText')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const LanguageSelector = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const availableLanguages = useAppSelector((state) => state.questionEditor.byId[questionId]?.availableLanguages);
  const isCopyMode = mode === ECreateQuestionMode.Copy || mode === ECreateQuestionMode.InTemplateCopy;

  return (
    <div
      className={clsx(mode === ECreateQuestionMode.InTemplateEdit && 'hidden', 'flex flex-col justify-start gap-4 p-1')}
    >
      <div
        className={clsx(
          mode === ECreateQuestionMode.InTemplateEdit && 'hidden',
          'hubert-vertical-center flex flex-row justify-start gap-4',
        )}
      >
        <HubertLabel className="text-sm font-medium" htmlFor="create-question-language">
          {t('selectors.language.label')}
        </HubertLabel>
        <LanguageCombobox
          onValueChange={(value) => {
            dispatch(updateLanguage({ questionId, language: value as TAvailableLanguageCodes }));
            dispatch(setText({ questionId, language: value as TAvailableLanguageCodes }));
          }}
          value={questionLanguage}
          allowLanguages={isCopyMode ? availableLanguages : undefined}
          disabled={mode && [ECreateQuestionMode.InTemplateEdit, ECreateQuestionMode.Edit].includes(mode)}
        />
      </div>
      <div>
        <MutedText text={t('selectors.language.hint')} />
      </div>
    </div>
  );
};

const ScopeNotSet = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  return (
    <div className={clsx('flex flex-col justify-start gap-4', 'p-1')}>
      <div className={clsx('hubert-vertical-center flex flex-row justify-start gap-4')}>
        <HubertLabel className="text-sm font-medium" htmlFor="create-question-scope">
          {t('selectors.scopeNotSet.label')}
        </HubertLabel>

        <div className={clsx('flex flex-row items-center')} id="create-question-scope">
          <HubertButton
            size="sm"
            variant="default"
            onClick={() => dispatch(updateVisibility({ questionId, visibility: 'global' }))}
          >
            {t('selectors.scopeNotSet.setButton')}
          </HubertButton>
        </div>
      </div>

      <div>
        <MutedText text={t('selectors.scopeNotSet.hint')} />
      </div>
    </div>
  );
};

const NO_ACCESS_GROUPS: AccessGroup[] = [];

export const ScopeSelector = () => {
  const { t } = useTranslation('Questionsv2');
  const { visibilityLabel } = useVisibilityLabels();
  const user = useUser();
  const { questionId } = useQuestionEditorContext();
  const dispatch = useAppDispatch();
  const clientId = useAppSelector((state) => state.questionEditor.byId[questionId].client_id);
  const visibility = useAppSelector((state) => state.questionEditor.byId[questionId].visibility);
  const access_groups_ids = useAppSelector((state) => state.questionEditor.byId[questionId].access_groups_ids);
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));

  const templateClientId = useTemplateClientId();
  const templateVisibility = useTemplateVisibility();
  const allowedModes = [...CREATE_MODES, ECreateQuestionMode.Edit];
  const isHubertAdmin = isHubertAdminUser(user);
  const { data } = useGetClientAccessGroupsQuery(
    { clientId: clientId || '' },
    { skip: !clientId || !isHubertAdmin || visibility === 'global' },
  );
  const accessGroupsToAssign = data?.accessGroups ?? NO_ACCESS_GROUPS;
  const singleAccessGroup = accessGroupsToAssign.length === 1;
  // In template edit mode, the selector is disabled
  const disabled = mode === ECreateQuestionMode.InTemplateEdit;
  const isTemplateMode = [ECreateQuestionMode.InTemplateCreate, ECreateQuestionMode.InTemplateCopy].includes(mode);

  useEffect(() => {
    const corrections = getAdminScopeCorrections(
      { mode, visibility, clientId, accessGroupIds: access_groups_ids, templateClientId },
      accessGroupsToAssign,
    );
    if (corrections.clientId) dispatch(updateClientId({ questionId, clientId: corrections.clientId }));
    if (corrections.accessGroupIds) {
      dispatch(updateAccessGroupIds({ questionId, accessGroupIds: corrections.accessGroupIds }));
    }
  }, [accessGroupsToAssign, clientId, dispatch, templateClientId, visibility, mode, access_groups_ids, questionId]);

  // Recruiters cannot see this selector
  if (!isHubertAdminUser(user)) {
    return null;
  }

  if (!allowedModes.includes(mode)) {
    return null;
  }

  if (!visibility) {
    return <ScopeNotSet />;
  }

  const visibilityItems = [
    { label: visibilityLabel('global'), value: 'global', precendence: 1 },
    { label: visibilityLabel('client'), value: 'client', precendence: 2 },
    { label: visibilityLabel('local'), value: 'local', precendence: 3 },
  ];

  const filteredScopes =
    isTemplateMode && !!templateVisibility
      ? visibilityItems.filter(
          (m) => m.precendence <= visibilityItems.find((x) => x.value === templateVisibility)!.precendence,
        )
      : undefined;
  return (
    <div className={clsx('flex flex-col justify-start gap-4', 'p-1')}>
      <div className={clsx('flex flex-row justify-start gap-16', 'hubert-vertical-center')}>
        <HubertLabel className="text-sm font-medium" htmlFor="create-question-scope">
          {t('selectors.scope.label')}
        </HubertLabel>
        <VisibilityCombobox
          isMulti={false}
          onValueChange={(value) => {
            dispatch(updateVisibility({ questionId, visibility: value as TVisibility }));
          }}
          value={visibility}
          disabled={disabled}
          items={filteredScopes}
        />
      </div>

      {['local', 'client'].includes(visibility) ? (
        <div className={clsx('flex flex-row justify-start gap-16', 'hubert-vertical-center')}>
          <HubertLabel className="text-sm font-medium" htmlFor="create-question-client-select">
            {t('selectors.scope.clientLabel')}
          </HubertLabel>
          <ClientCombobox
            id="create-question-client-select"
            isMulti={false}
            onValueChange={(value) => dispatch(updateClientId({ questionId, clientId: value as string }))}
            value={clientId}
            disabled={disabled}
          />
        </div>
      ) : null}

      {visibility === 'local' ? (
        <div className={clsx('flex flex-row justify-start gap-16', 'hubert-vertical-center')}>
          <HubertLabel className="text-sm font-medium" htmlFor="create-question-access-groups-select">
            {t('selectors.scope.accessGroups')}
          </HubertLabel>
          {singleAccessGroup ? (
            <AccessGroupCombobox
              id="create-question-access-groups-select"
              isMulti={true}
              value={access_groups_ids}
              options={accessGroupsToAssign}
              disabled={true}
            />
          ) : (
            <AccessGroupCombobox
              id="create-question-access-groups-select"
              isMulti={true}
              onValueChange={(value) =>
                dispatch(updateAccessGroupIds({ questionId, accessGroupIds: value as string[] }))
              }
              value={access_groups_ids}
              options={accessGroupsToAssign}
              disabled={disabled}
            />
          )}
        </div>
      ) : null}
      <div>
        <MutedText text={filteredScopes ? t('selectors.scope.hintFiltered') : t('selectors.scope.hintFull')} />
      </div>
    </div>
  );
};

export const RecruiterScopeSelector = ({ user }: { user: TRecruiterAppUser }) => {
  const { t } = useTranslation('Questionsv2');
  const { visibilityLabel } = useVisibilityLabels();
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const clientId = useAppSelector((state) => state.questionEditor.byId[questionId].client_id);
  const visibility = useAppSelector((state) => state.questionEditor.byId[questionId].visibility);
  const access_groups_ids = useAppSelector((state) => state.questionEditor.byId[questionId].access_groups_ids);
  const hasQuestionUpdatePermission = useAppSelector(
    (state) => state.questionEditor.byId[questionId].hasQuestionUpdatePermission,
  );
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateReferenceId = useTemplateReferenceId();
  const templateVisibility = useTemplateVisibility();
  const [groupsAwaitingConfirmation, setGroupsAwaitingConfirmation] = useState<string[]>();

  const disabled = mode === ECreateQuestionMode.InTemplateEdit;
  const isTemplateMode = [ECreateQuestionMode.InTemplateCreate, ECreateQuestionMode.InTemplateCopy].includes(mode);
  const keepsTemplateAccessGroups = isTemplateMode && templateVisibility === 'local';
  const isCreating = CREATE_MODES.includes(mode);

  const context: TRecruiterScopeContext = {
    mode,
    visibility,
    clientId,
    accessGroupIds: access_groups_ids,
    hasQuestionUpdatePermission,
    templateReferenceId,
    templateVisibility,
  };
  const canSeePicker = canSeeRecruiterScopePicker(user, context);
  const canSwitchOrganizationLevel = canChangeOrganizationLevel(user);
  const anyClientGroup = picksAnyClientGroup(user);
  const { data: clientGroups } = useGetClientAccessGroupsQuery({ clientId: user.client_id }, { skip: !anyClientGroup });
  const accessGroupOptions = anyClientGroup
    ? (clientGroups?.accessGroups ?? []).map((x) => ({ id: x.id, name: x.name }))
    : getRecruiterScopeOptions(user);
  const otherAccessGroupIds = anyClientGroup ? [] : getOtherAccessGroupIds(user, access_groups_ids);
  const chosenMemberGroupIds = (access_groups_ids ?? []).filter((id) => !otherAccessGroupIds.includes(id));
  const needsQuestionCreateGroup = chosenGroupsLackQuestionCreate(user, context);

  useEffect(() => {
    const corrections = getRecruiterScopeCorrections(user, {
      mode,
      visibility,
      clientId,
      accessGroupIds: access_groups_ids,
      hasQuestionUpdatePermission,
      templateReferenceId,
      templateVisibility,
    });
    if (corrections.visibility) dispatch(updateVisibility({ questionId, visibility: corrections.visibility }));
    if (corrections.clientId) dispatch(updateClientId({ questionId, clientId: corrections.clientId }));
    if (corrections.accessGroupIds) {
      dispatch(updateAccessGroupIds({ questionId, accessGroupIds: corrections.accessGroupIds }));
    }
  }, [
    user,
    mode,
    visibility,
    clientId,
    access_groups_ids,
    hasQuestionUpdatePermission,
    templateReferenceId,
    templateVisibility,
    dispatch,
    questionId,
  ]);

  const saveAccessGroups = (accessGroupIds: string[]) => {
    setGroupsAwaitingConfirmation(undefined);
    dispatch(updateAccessGroupIds({ questionId, accessGroupIds }));
  };

  const onChooseMemberGroups = (memberGroupIds: string[]) => {
    const accessGroupIds = anyClientGroup
      ? memberGroupIds
      : applyMemberGroupChoice(user, access_groups_ids, memberGroupIds);
    if (!isCreating && losesQuestionUpdate(user, access_groups_ids, accessGroupIds)) {
      setGroupsAwaitingConfirmation(accessGroupIds);
      return;
    }
    saveAccessGroups(accessGroupIds);
  };

  if (!canSeePicker) return null;
  return (
    <div className={clsx('flex flex-col justify-start gap-4', 'p-1')}>
      {canSwitchOrganizationLevel && (
        <div className={clsx('flex flex-row justify-start gap-16', 'hubert-vertical-center')}>
          <HubertLabel className="text-sm font-medium" htmlFor="create-question-scope">
            {t('selectors.recruiterScope.label')}
          </HubertLabel>
          <div className={clsx('flex flex-row items-center gap-3')} id="create-question-scope">
            <span className={clsx('text-xs', visibility === 'local' && 'font-semibold')}>
              {visibilityLabel('local')}
            </span>
            <HubertSwitch
              checked={!visibility || visibility === 'client' ? true : false}
              onCheckedChange={(value) =>
                dispatch(updateVisibility({ questionId, visibility: value ? 'client' : 'local' }))
              }
              disabled={disabled || keepsTemplateAccessGroups}
            />
            <span className={clsx('text-xs', visibility === 'client' && 'font-semibold')}>
              {visibilityLabel('client')}
            </span>
          </div>
          <div>
            <MutedText text={t('selectors.recruiterScope.hint')} />
          </div>
        </div>
      )}
      {visibility === 'local' ? (
        <div className="flex flex-col gap-2">
          <div className="hubert-vertical-center flex flex-row justify-start gap-16">
            <HubertLabel className="text-sm font-medium" htmlFor="create-question-access-groups-select">
              {t('selectors.recruiterScope.accessGroups')}
            </HubertLabel>
            <AccessGroupCombobox
              id="create-question-access-groups-select"
              isMulti={true}
              onValueChange={(value) => onChooseMemberGroups(value as string[])}
              value={chosenMemberGroupIds}
              disabled={disabled}
              options={accessGroupOptions}
            />
            {otherAccessGroupIds.length ? (
              <MutedText text={t('selectors.recruiterScope.otherGroups', { count: otherAccessGroupIds.length })} />
            ) : null}
          </div>
          {needsQuestionCreateGroup ? (
            <p className="text-xs text-amber-700">{t('selectors.recruiterScope.needsQuestionCreate')}</p>
          ) : null}
          {groupsAwaitingConfirmation ? (
            <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <span>{t('selectors.recruiterScope.lockOut.message')}</span>
              <div className="flex flex-row gap-2">
                <HubertButton size="sm" variant="secondary" onClick={() => setGroupsAwaitingConfirmation(undefined)}>
                  {t('selectors.recruiterScope.lockOut.cancel')}
                </HubertButton>
                <HubertButton size="sm" onClick={() => saveAccessGroups(groupsAwaitingConfirmation)}>
                  {t('selectors.recruiterScope.lockOut.confirm')}
                </HubertButton>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
